// Broker Mirror Service - API calls for broker link management and trade mirroring

import { useAuthStore } from "../stores/authStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

const API_BASE = import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// =============================================================================
// Types
// =============================================================================

export type BrokerType = "schwab" | "ibkr" | "tradestation" | "tastytrade" | "webull";
export type BrokerLinkStatus = "pending" | "active" | "token_expiring" | "token_expired" | "paused" | "error" | "disconnected";
export type MirrorQueueStatus = "pending" | "processing" | "completed" | "failed" | "skipped";

export interface BrokerLinkSummary {
  id: string;
  brokerage_account_id: string;
  broker_type: BrokerType;
  broker_account_number?: string;
  status: BrokerLinkStatus;
  mirror_enabled: boolean;
  access_token_expires_at?: string;
  refresh_token_expires_at?: string;
  last_token_refresh_at?: string;
  last_mirror_at?: string;
  last_mirror_error?: string;
  created_at: string;
  updated_at: string;
  today_mirrored: number;
  today_completed: number;
  today_failed: number;
}

export interface MirrorStatusSummary {
  total_links: number;
  active_links: number;
  today_enqueued: number;
  today_completed: number;
  today_failed: number;
  today_pending: number;
  links: BrokerLinkSummary[];
}

export interface MirrorQueueEntry {
  id: string;
  broker_link_id: string;
  broker_type: BrokerType;
  execution_log_id?: string;
  scheduler_run_id?: string;
  symbol: string;
  side: string;
  qty: string;
  alpaca_fill_price: string;
  alpaca_order_id?: string;
  status: MirrorQueueStatus;
  broker_order_id?: string;
  broker_fill_price?: string;
  broker_fill_qty?: string;
  error_message?: string;
  attempts: number;
  max_attempts: number;
  created_at: string;
  picked_up_at?: string;
  completed_at?: string;
}

export interface MirrorHistoryResult {
  entries: MirrorQueueEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface CreateBrokerLinkRequest {
  brokerage_account_id: string;
  broker_type: BrokerType;
  broker_account_number?: string;
  app_key: string;
  app_secret: string;
}

export interface InitiateOAuthRequest {
  broker_link_id: string;
}

export interface OAuthInitiateResponse {
  authorization_url: string;
  broker_link_id: string;
}

// =============================================================================
// Broker Configuration Abstraction
// =============================================================================

export interface BrokerFieldConfig {
  name: string;
  label: string;
  type: "text" | "password";
  placeholder: string;
  helpText?: string;
}

export interface BrokerConfig {
  type: BrokerType;
  displayName: string;
  description: string;
  authFlow: "oauth" | "api_key" | "none";
  implemented: boolean;
  fields: BrokerFieldConfig[];
  oauthCallbackNote?: string;
  learnMoreUrl?: string;
}

/**
 * Registry of supported brokers and their configuration.
 * To add a new broker, add an entry here — no other UI changes needed.
 */
export const BROKER_CONFIGS: Record<string, BrokerConfig> = {
  schwab: {
    type: "schwab",
    displayName: "Charles Schwab",
    description: "Mirror trades to your Schwab brokerage account via OAuth. Requires a Schwab developer app.",
    authFlow: "oauth",
    implemented: true,
    fields: [
      {
        name: "app_key",
        label: "Client ID",
        type: "text",
        placeholder: "Your Schwab developer Client ID",
        helpText: "From your Schwab Developer Portal app registration.",
      },
      {
        name: "app_secret",
        label: "Client Secret",
        type: "password",
        placeholder: "Your Schwab developer Client Secret",
        helpText: "Keep this secret. Never share it.",
      },
    ],
    oauthCallbackNote: "After entering your credentials, you'll be redirected to Schwab to authorize the connection. You'll be brought back here automatically.",
    learnMoreUrl: "https://developer.schwab.com/",
  },
  ibkr: {
    type: "ibkr",
    displayName: "Interactive Brokers",
    description: "Mirror trades to your IBKR account. Requires IBKR Client Portal API or TWS.",
    authFlow: "api_key",
    implemented: false,
    fields: [],
  },
  tastytrade: {
    type: "tastytrade",
    displayName: "Tastytrade",
    description: "Mirror trades to your Tastytrade account.",
    authFlow: "api_key",
    implemented: false,
    fields: [],
  },
};

export const SUPPORTED_BROKERS = Object.values(BROKER_CONFIGS);

export function getBrokerConfig(brokerType: BrokerType): BrokerConfig | undefined {
  return BROKER_CONFIGS[brokerType];
}

// =============================================================================
// Helper Functions
// =============================================================================

function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().accessToken;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));

    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      console.warn("[brokerMirrorService] 401 Unauthorized - triggering session expired modal");
      useSessionExpiredStore.getState().setExpired();
    }

    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// =============================================================================
// Broker Link API
// =============================================================================

const LINKS_BASE = `${API_BASE}/api/v1/brokerage/links`;

/** List all broker links for the current user */
export async function listBrokerLinks(): Promise<BrokerLinkSummary[]> {
  const response = await fetch(LINKS_BASE, {
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<{ links: BrokerLinkSummary[] }>(response);
  return data.links || [];
}

/** Create a new broker link (step 1 before OAuth) */
export async function createBrokerLink(request: CreateBrokerLinkRequest): Promise<BrokerLinkSummary> {
  const response = await fetch(LINKS_BASE, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<BrokerLinkSummary>(response);
}

/** Get a specific broker link */
export async function getBrokerLink(linkId: string): Promise<BrokerLinkSummary> {
  const response = await fetch(`${LINKS_BASE}/${linkId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<BrokerLinkSummary>(response);
}

/** Disconnect (delete) a broker link */
export async function disconnectBrokerLink(linkId: string): Promise<void> {
  const response = await fetch(`${LINKS_BASE}/${linkId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    if (response.status === 401) {
      useSessionExpiredStore.getState().setExpired();
    }
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

/** Pause mirroring for a link */
export async function pauseBrokerLink(linkId: string): Promise<void> {
  const response = await fetch(`${LINKS_BASE}/${linkId}/pause`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    if (response.status === 401) {
      useSessionExpiredStore.getState().setExpired();
    }
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

/** Resume mirroring for a link */
export async function resumeBrokerLink(linkId: string): Promise<void> {
  const response = await fetch(`${LINKS_BASE}/${linkId}/resume`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    if (response.status === 401) {
      useSessionExpiredStore.getState().setExpired();
    }
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

/** Get overall mirror status summary */
export async function getMirrorStatus(): Promise<MirrorStatusSummary> {
  const response = await fetch(`${LINKS_BASE}/mirror-status`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<MirrorStatusSummary>(response);
}

/** Get all mirror history across ALL broker links for the current user */
export async function getAllMirrorHistory(
  params?: { page?: number; limit?: number; status?: MirrorQueueStatus; symbol?: string; side?: string; broker_type?: BrokerType }
): Promise<MirrorHistoryResult> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.status) searchParams.set("status", params.status);
  if (params?.symbol) searchParams.set("symbol", params.symbol);
  if (params?.side) searchParams.set("side", params.side);
  if (params?.broker_type) searchParams.set("broker_type", params.broker_type);

  const qs = searchParams.toString();
  const url = `${LINKS_BASE}/mirror-history${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return handleResponse<MirrorHistoryResult>(response);
}

/** Initiate Schwab OAuth flow — returns the URL to redirect the user to */
export async function initiateSchwabOAuth(brokerLinkId: string): Promise<OAuthInitiateResponse> {
  const response = await fetch(`${LINKS_BASE}/schwab/initiate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ broker_link_id: brokerLinkId }),
  });
  return handleResponse<OAuthInitiateResponse>(response);
}

/** Get mirror history for a specific link */
export async function getMirrorHistory(
  linkId: string,
  params?: { page?: number; limit?: number; status?: MirrorQueueStatus; symbol?: string; side?: string }
): Promise<MirrorHistoryResult> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.status) searchParams.set("status", params.status);
  if (params?.symbol) searchParams.set("symbol", params.symbol);
  if (params?.side) searchParams.set("side", params.side);

  const qs = searchParams.toString();
  const url = `${LINKS_BASE}/${linkId}/mirror-history${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return handleResponse<MirrorHistoryResult>(response);
}

// =============================================================================
// Display Helpers
// =============================================================================

export function getBrokerDisplayName(brokerType: BrokerType): string {
  return BROKER_CONFIGS[brokerType]?.displayName || brokerType;
}

export function getLinkStatusDisplay(status: BrokerLinkStatus): { label: string; colorClass: string } {
  switch (status) {
    case "active":
      return { label: "Active", colorClass: "bg-accent-500/20 text-accent-400" };
    case "pending":
      return { label: "Pending OAuth", colorClass: "bg-yellow-500/20 text-yellow-400" };
    case "token_expiring":
      return { label: "Token Expiring", colorClass: "bg-yellow-500/20 text-yellow-400" };
    case "token_expired":
      return { label: "Token Expired", colorClass: "bg-red-500/20 text-red-400" };
    case "paused":
      return { label: "Paused", colorClass: "bg-surface-600/50 text-surface-300" };
    case "error":
      return { label: "Error", colorClass: "bg-red-500/20 text-red-400" };
    case "disconnected":
      return { label: "Disconnected", colorClass: "bg-surface-600/50 text-surface-400" };
    default:
      return { label: status, colorClass: "bg-surface-600/50 text-surface-300" };
  }
}

export function getMirrorQueueStatusDisplay(status: MirrorQueueStatus): { label: string; colorClass: string } {
  switch (status) {
    case "pending":
      return { label: "Pending", colorClass: "bg-yellow-500/20 text-yellow-400" };
    case "processing":
      return { label: "Processing", colorClass: "bg-blue-500/20 text-blue-400" };
    case "completed":
      return { label: "Completed", colorClass: "bg-accent-500/20 text-accent-400" };
    case "failed":
      return { label: "Failed", colorClass: "bg-red-500/20 text-red-400" };
    case "skipped":
      return { label: "Skipped", colorClass: "bg-surface-600/50 text-surface-300" };
    default:
      return { label: status, colorClass: "bg-surface-600/50 text-surface-300" };
  }
}
